import React, { useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { staffLogin } from "../../utils/apiClient";
import { useTheme } from "../../ThemeContext";
import { Button } from "@voicecare.ai/ui-library";

const ChangePasswordDialog = ({ open, onClose }) => {
  const [oldPassword, setOldPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [validatingPassword, setValidatingPassword] = useState(false);

  const navigate = useNavigate();
  const { themeMode, currentTheme } = useTheme();

  // Get email from session storage
  const getEmail = () => {
    try {
      const staffInfo = sessionStorage.getItem("staff_info");
      if (staffInfo) {
        const parsedStaffInfo = JSON.parse(staffInfo);
        return parsedStaffInfo.email;
      }
      return null;
    } catch (error) {
      console.error("Error parsing staff info:", error);
      return null;
    }
  };

  const handleCloseDialog = () => {
    setOldPassword("");
    setPasswordError("");
    setShowOldPassword(false);
    onClose();
  };

  const handleValidatePassword = async () => {
    if (!oldPassword.trim()) {
      setPasswordError("Please enter your current password");
      return;
    }

    setPasswordError("");
    setValidatingPassword(true);

    try {
      const email = getEmail();
      if (!email) {
        message.error("Email not found. Please login again.");
        handleCloseDialog();
        return;
      }

      const response = await staffLogin({
        email: email,
        password: oldPassword,
      });

      if (response.statusCode === "SUCCESS") {
        message.success("Password validated successfully!");
        handleCloseDialog();
        // Navigate to change-password route
        navigate("/change-password");
      } else {
        setPasswordError(response.message || "Invalid password. Please try again.");
      }
    } catch (error) {
      console.error("Error validating password:", error);
      if (error.response && error.response.data) {
        setPasswordError(error.response.data.message || "Invalid password. Please try again.");
      } else {
        setPasswordError("Invalid password. Please try again.");
      }
    } finally {
      setValidatingPassword(false);
    }
  };

  const handleOldPasswordChange = (event) => {
    setOldPassword(event.target.value);
    if (passwordError) {
      setPasswordError("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
          color: currentTheme.homePage?.textColor || (themeMode === "dark" ? "#ffffff" : "#000000"),
          borderRadius: "16px",
          border: themeMode === "dark" ? "1px solid #302d4c" : "1px solid #e9e8ea",
          fontFamily: '"Satoshi", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
          boxShadow: themeMode === "dark" 
            ? "0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.1)"
            : "0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0,0,0,0.05)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <Typography
            variant="h4"
            sx={{
              color: currentTheme.text?.primary || (themeMode === "dark" ? "#ffffff" : "#000000"),
              marginTop: "2rem",
              fontWeight: 600,
              fontFamily: '"Satoshi", sans-serif',
              fontSize: "1.75rem",
              textAlign: "center",
              "@media (max-width: 480px)": {
                fontSize: "1.4rem",
              },
            }}
          >
            Change Password
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <Box component="form" sx={{ mt: 2 }}>
          <Typography
            variant="body1"
            sx={{
              color: currentTheme.text?.primary || (themeMode === "dark" ? "#ffffff" : "#000000"),
              mb: 2,
              fontFamily: '"Satoshi", sans-serif',
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}
          >
            Please enter your current password to proceed.
          </Typography>

          {/* Old Password Field */}
          <Typography
            variant="body1"
            sx={{
              color: currentTheme.text?.primary || (themeMode === "dark" ? "#ffffff" : "#000000"),
              mb: 1,
              fontFamily: '"Satoshi", sans-serif',
              fontSize: "1rem",
              fontWeight: 500,
            }}
          >
            Current Password*
          </Typography>
          <TextField
            fullWidth
            type={showOldPassword ? "text" : "password"}
            value={oldPassword}
            onChange={handleOldPasswordChange}
            error={!!passwordError}
            helperText={passwordError}
            placeholder="Enter your current password"
            disabled={validatingPassword}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !validatingPassword) {
                handleValidatePassword();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    edge="end"
                    sx={{
                      color: currentTheme.text?.primary || (themeMode === "dark" ? "#ffffff" : "#000000"),
                    }}
                  >
                    {showOldPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                backgroundColor: themeMode === "dark" ? "rgba(255,255,255,0.05)" : "#ffffff",
                borderRadius: 2,
                fontFamily: '"Satoshi", sans-serif',
                "& fieldset": {
                  borderColor: passwordError 
                    ? "#d32f2f" 
                    : (themeMode === "dark" ? "#302d4c" : "#e9e8ea"),
                },
                "&:hover fieldset": {
                  borderColor: passwordError 
                    ? "#d32f2f" 
                    : (themeMode === "dark" ? "#444" : "#ccc"),
                },
                "&.Mui-focused fieldset": {
                  borderColor: passwordError 
                    ? "#d32f2f" 
                    : (themeMode === "dark" ? "#666" : "#999"),
                },
              },
              "& .MuiInputBase-input": {
                color: currentTheme.text?.primary || (themeMode === "dark" ? "#ffffff" : "#000000"),
                fontFamily: '"Satoshi", sans-serif',
              },
              "& .MuiFormHelperText-root": {
                color: passwordError ? "#d32f2f" : (themeMode === "dark" ? "#999" : "#666"),
                fontFamily: '"Satoshi", sans-serif',
              },
            }}
          />

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleCloseDialog}
              label="Cancel"
              disabled={validatingPassword}
              backgroundColor={currentTheme.loginPage?.textBoxBackground || (themeMode === "dark" ? "#302d4c" : "#f5f5f5")}
              color={currentTheme.text?.primary || (themeMode === "dark" ? "#ffffff" : "#000000")}
              style={{ borderRadius: "6px" }}
            />
            <Button
              variant="contained"
              onClick={handleValidatePassword}
              label={validatingPassword ? "Validating..." : "Continue"}
              disabled={validatingPassword || !oldPassword.trim()}
              style={{ borderRadius: "6px" }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;

